import React, { useState } from 'react';

import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';
import { useEligibleSSRVariantName } from 'shared/components/ExperimentV2/helpers';
import { useSelectedSKU } from 'shared/store/details/hooks/selectors';
import { useSelector } from 'react-redux';
import { getProductData } from 'shared/store/details/selectors';
import {
  MULTI_SELLER_EXPERIMENT_ID,
  MULTI_SELLER_TREATMENT_VARIANT,
} from 'shared/constants/multiSeller';

import { SellerInfo, SellerName } from './styles';
import MultiSellerBottomSheet from 'shared/components/MultiSellerBottomSheet';
import { getSelectedVendorId, resolveVendorId } from 'shared/helpers/multiSeller';

interface MultiSellerInfoProps {
  selectedSKU?: ProductDetail.ISizeOption;
  defaultVendorId?: string;
  isOnCart?: boolean;
  isSizeSelector?: boolean;
  marginBottom?: string;
  marginTop?: string;
  onAddToBagSuccess?: () => void;
  hideToast?: boolean;
  onOpenSellerSheet?: () => void;
  onSellerSheetVisibilityChange?: (isVisible: boolean) => void;
}

const MultiSellerInfo = ({
  selectedSKU: selectedSKUProp,
  defaultVendorId: defaultVendorIdProp,
  isOnCart = false,
  isSizeSelector = false,
  onAddToBagSuccess,
  hideToast,
  onOpenSellerSheet,
  marginBottom = 'spacing0',
  marginTop = 'spacing0',
  onSellerSheetVisibilityChange,
}: MultiSellerInfoProps) => {
  const multiSellerEnabled = useRemoteConfig('multiSellerEnabled', false);
  const multiSellerConfig = useRemoteConfig('multiSeller');
  const { isEligible, variant } = useEligibleSSRVariantName(MULTI_SELLER_EXPERIMENT_ID);
  const reduxSelectedSKU = useSelectedSKU();
  const product = useSelector(getProductData);
  const selectedSKU = selectedSKUProp || reduxSelectedSKU;
  const defaultVendorId = defaultVendorIdProp || product?.vendor_id || '';
  const selectedVendorId = getSelectedVendorId(selectedSKU, defaultVendorId);
  const vendors = selectedSKU?.view_more_section?.vendors || {};
  const resolvedVendorId = resolveVendorId(vendors, selectedVendorId, defaultVendorId);
  const vendor = vendors[resolvedVendorId || ''];
  const vendorCount = Object.keys(vendors).length;
  const sellerName = vendor?.vendor_name || '';
  const displayedSellerName =
    sellerName.length > 32 ? `${sellerName.slice(0, 32)}...` : sellerName;
  const [showSellerSheet, setShowSellerSheet] = useState(false);
  const shouldShowSellerInfo =
    multiSellerEnabled &&
    multiSellerConfig?.enabled &&
    (isOnCart ? multiSellerConfig?.cart?.enabled : multiSellerConfig?.pdp?.enabled) &&
    isEligible &&
    variant === MULTI_SELLER_TREATMENT_VARIANT &&
    (!isSizeSelector ||
      vendorCount > 1 ||
      multiSellerConfig?.showSingleSellerNameInBottomSheet) &&
    Boolean(sellerName);

  if (!shouldShowSellerInfo) return null;

  return (
    <SellerInfo marginBottom={marginBottom} marginTop={marginTop} data-test="multi-seller-info">
      Sold by:{' '}
      <SellerName
        type="button"
        data-test="multi-seller-trigger"
        onClick={() => {
          if (onOpenSellerSheet) {
            onOpenSellerSheet();
            return;
          }
          setShowSellerSheet(true);
          onSellerSheetVisibilityChange?.(true);
        }}
      >
        {displayedSellerName}
        {vendorCount > 1 && ` | +${vendorCount - 1} more`}
      </SellerName>
      {!onOpenSellerSheet && showSellerSheet && (
        <MultiSellerBottomSheet
          vendors={vendors}
          selectedSKU={selectedSKU}
          defaultVendorId={resolvedVendorId}
          onClose={() => {
            setShowSellerSheet(false);
            onSellerSheetVisibilityChange?.(false);
          }}
          onAddToBagSuccess={onAddToBagSuccess}
          hideToast={hideToast}
          isSizeSelector={isSizeSelector}
        />
      )}
    </SellerInfo>
  );
};

export default MultiSellerInfo;
